import classnames from 'classnames'
import { css } from 'emotion'
import { Product } from 'even-ts-static'
import * as React from 'react'

import { formatCurrency } from 'embed/utils'
import { DepositsOffer } from 'embed/constants/models'

import ErrorIndicator from 'embed/components/ErrorIndicator'
import LoadingIndicator, { BRAND_COLOR } from 'embed/components/LoadingIndicator'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { Tooltip } from 'embed/components/Tooltip'

import { DepositAmountInput } from './DepositAmountInput'
import { DepositsOfferListItem } from './OfferListItem'

export const CASH_RETURN_PREFIX = '$'
export const DEFAULT_AMOUNT = 5000
export const NATIONAL_AVERAGE_APY = 0.0009

interface Props {
  offer?: DepositsOffer
  depositAmountIntent?: number
  dispatchOfferCta?: (offer: DepositsOffer) => void
  error: boolean
}

const styles = require('./styles')

export const SavingsCalculatorView: React.FC<Props> = ({ depositAmountIntent, error, offer }) => {
  if (offer === undefined) {
    return <LoadingIndicator brandColor={BRAND_COLOR.TRUE} loadingLabel="Finding Offer" product={Product.Deposits} />
  } else if (error) {
    return <ErrorIndicator errorLabel="This offer is no longer available." />
  } else {
    const cashReturn = formatCurrency(
      (depositAmountIntent !== undefined && !isNaN(depositAmountIntent) ? depositAmountIntent : DEFAULT_AMOUNT) *
        NATIONAL_AVERAGE_APY,
    )

    // We're temporarily hard-coding the offer URL (https://evenfinancial.atlassian.net/browse/FE-1407)
    const offerWithHardCodedUrl = {
      ...offer,
      url:
        'https://offers.evenfinancial.com/partner/ref/2a5650a3-ebce-421b-b0e1-a35f94003b8d/80e212a9-bc92-4939-8885-08b03dfe4e18',
    }

    return (
      <ThemeConsumer>
        {({ colorTextSecondary }) => (
          <div>
            <DepositAmountInput />
            <div className={styles.header}>
              <div className={styles.columnTitle}>National Average</div>
              <div className={styles.columnReturn}>
                <div
                  className={classnames(
                    styles.statTitle,
                    css`
                      color: ${colorTextSecondary};
                    `,
                  )}
                >
                  Estimated Annual Cash Return
                </div>
                <div
                  className={classnames(
                    styles.stat,
                    css`
                      color: ${colorTextSecondary};
                    `,
                  )}
                >
                  {CASH_RETURN_PREFIX}
                  {cashReturn}
                </div>
              </div>
              <div className={styles.columnApy}>
                <div
                  className={classnames(
                    styles.statTitle,
                    css`
                      color: ${colorTextSecondary};
                    `,
                  )}
                >
                  <Tooltip
                    overlay={
                      <>
                        National average is based on data from the{' '}
                        <a href="https://www.fdic.gov/regulations/resources/rates/" target="_blank">
                          FDIC
                        </a>
                        .
                      </>
                    }
                    placement="bottom"
                  >
                    <a className={styles.apyTooltipTarget}>APY</a>
                  </Tooltip>
                </div>
                <div
                  className={classnames(
                    styles.stat,
                    css`
                      color: ${colorTextSecondary};
                    `,
                  )}
                >
                  0.09%
                </div>
              </div>
            </div>
            <DepositsOfferListItem offer={offerWithHardCodedUrl} />
            <p className={styles.disclaimer}>Disclaimer: Return is based on current rates only</p>
          </div>
        )}
      </ThemeConsumer>
    )
  }
}
